import { useCallback, useEffect, useRef, useState } from "react"
import { Button, Select } from "antd"
import { useTranslation } from "react-i18next"
import AudioTroubleshootModal from "components/dumb/AudioTroubleshootModal"
import { storage } from "services/config/storage"
import { useIsMobileDevice } from "hooks"
import { Cross, Dropdown, MicrophoneIcon } from "assets/icons"
import analytics from "services/config/analytics"
import "./style.scss"

export const MicTest = ({ onSuccess = () => { }, loading }) => {
  const [availableDevices, setAvailableDevices] = useState([])
  const [selectedDevice, setSelectedDevice] = useState("")
  const [audioSrc, setAudioSrc] = useState(null)
  const isMobile = useIsMobileDevice()
  const { t } = useTranslation()

  const saveAudioDevice = (deviceId) => {
    if (deviceId) {
      storage.set.micDevice(deviceId)
      setSelectedDevice(deviceId)
    }
  }

  const audioBlobToUrl = (blob) => {
    const audioUrl = URL.createObjectURL(blob)
    setAudioSrc(audioUrl)
  }

  const retest = () => {
    analytics.track('mic_test_retry')
    setAudioSrc(null)
  }

  useEffect(() => {
    const handleDevices = (mediaDevices) => {
      const audioSources = mediaDevices.filter(
        ({ kind }) => kind === "audioinput"
      )
      setAvailableDevices(audioSources)
      if (audioSources.length && !selectedDevice) {
        const [firstSource] = audioSources
        saveAudioDevice(firstSource.deviceId)
      }
    }

    if (!navigator.mediaDevices?.enumerateDevices) {
      console.log("enumerateDevices() not supported.")
    } else {
      navigator.mediaDevices.enumerateDevices().then(handleDevices)
    }
  }, [])

  return (
    <>
      <div className={`audio-test-div ${isMobile ? 'flex flex-col items-center' : ''}`} style={isMobile ? { textAlign: 'center' } : {}}>
        <div className={`flex ${isMobile ? 'justify-center' : 'items-center'}`}>
          <h1 className={`color-primary font-medium ${isMobile ? 'size-24' : ''}`}>
            {t("mic_test.title")}
          </h1>
        </div>

        <div className={isMobile ? 'mt-10' : 'mt-20'}>
          <h4 className={`color-primary font-medium opacity-70 ${isMobile ? 'size-14 mb-6' : ''}`}>
            {t("mic_test.select_microphone")}
          </h4>
          <div className={`flex ${isMobile ? 'mt-24 justify-center' : 'mt-2 items-center'}`}>
            <Select
              className={`size-14 width-${isMobile ? "300" : "400"}`}
              suffixIcon={<Dropdown />}
              value={selectedDevice}
              options={availableDevices.map(({ label, deviceId }) => ({
                label,
                value: deviceId,
              }))}
              onChange={saveAudioDevice}
            />
          </div>
          {audioSrc ? (
            <div className="mx-auto mt-12 w-full lg:w-1/2">
              <AudioPlayer src={audioSrc} isMobile={isMobile} />
            </div>
          ) : (
            <AudioRecorder
              hideTimer
              audioDeviceId={selectedDevice}
              getBlobData={audioBlobToUrl}
              onSuccess={onSuccess}
              isMobile={isMobile}
              loading={loading}
            />
          )}
        </div>

        {audioSrc && (
          <div className="mt-40">
            <p className="size-14 color-primary font-medium opacity-70">{t("mic_test.am_i_audible")}</p>
            <div className={`flex items-stretch ${isMobile ? 'justify-center' : ''}`}>
              <Button
                type="primary"
                className="br-20 width-50"
                onClick={onSuccess}
              >
                {t("yes")}
              </Button>
              <button
                className="secondary-btn br-20 pb-6 pt-6 pl-10 pr-10 ml-10 cursor-pointer"
                onClick={retest}
              >
                {t("mic_test.try_again")}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

const AudioPlayer = ({ src, isMobile }) => {

  return (
    <div className={`flex items-center mt-20 ${isMobile ? 'justify-center' : 'justify-start'}`}>
      <audio
        controls={true}
        autoPlay={true}
        className={`width-${isMobile ? "300" : "400"}`}
      >
        <source src={src} type="audio/mp3" />
      </audio>
    </div>
  )
}

// Mic level (0-255 average) above which a frame counts as containing sound.
const AUDIO_VOLUME_THRESHOLD = 10
// A steady buzz, hum or fan sits above the volume threshold on every frame, so
// loudness alone cannot tell it apart from speech. Speech rises and falls
// between syllables, so we also require the level to vary: the gap between the
// quiet and loud ends of the recording must exceed this (0-255 scale).
//
// Kept low: this is an average across all FFT bins, and speech occupies only
// ~12% of them, so even a clear voice produces a much smaller swing than it
// sounds. A steady tone still measures near zero range, so the two separate
// cleanly without rejecting quiet speakers.
const MIN_LEVEL_RANGE = 8
// Frames needed before the variation test is meaningful (~1s of audio).
const MIN_FRAMES_FOR_VARIANCE = 20

const AudioRecorder = ({
  audioDeviceId,
  getBlobData = () => { },
  onStatusChange = () => { },
  isMobile,
  onSuccess,
  loading,
}) => {
  const [isRecording, setIsRecording] = useState(false)
  const [recorder, setRecorder] = useState(null)
  const [volumeLevel, setVolumeLevel] = useState(0)
  const [audioContext, setAudioContext] = useState(null)
  const [errorMessage, setErrorMessage] = useState(false)
  const [showTroubleshoot, setShowTroubleshoot] = useState(false)
  const buttonRef = useRef(null)
  // Loudest and quietest frames seen this recording, used to measure how much
  // the level varies. Reset on every start so one recording cannot carry its
  // result into the next.
  const loudestFrameRef = useRef(0)
  const quietestFrameRef = useRef(Infinity)
  const frameCountRef = useRef(0)
  const { t } = useTranslation()

  const requestRecorder = useCallback(async () => {
    const deviceId = audioDeviceId
    const constraints = { audio: deviceId ? { deviceId } : true }
    const stream = await navigator.mediaDevices.getUserMedia(constraints)

    // logic for showing microphone input level
    const audioContext = new AudioContext()
    const analyser = audioContext.createAnalyser()
    const microphone = audioContext.createMediaStreamSource(stream)
    const scriptProcessor = audioContext.createScriptProcessor(2048, 1, 1)

    analyser.smoothingTimeConstant = 0.8
    analyser.fftSize = 1024

    microphone.connect(analyser)
    analyser.connect(scriptProcessor)
    scriptProcessor.connect(audioContext.destination)
    scriptProcessor.onaudioprocess = function () {
      const array = new Uint8Array(analyser.frequencyBinCount)
      analyser.getByteFrequencyData(array)
      const arraySum = array.reduce((a, value) => a + value, 0)
      const average = arraySum / array.length
      setVolumeLevel(average)

      // Track the spread between quiet and loud frames. A steady tone keeps
      // both ends close together; speech pushes them apart.
      frameCountRef.current += 1
      if (average > loudestFrameRef.current) loudestFrameRef.current = average
      if (average < quietestFrameRef.current) quietestFrameRef.current = average
    }

    // Saving context to close it once the recording is stopped
    setAudioContext(audioContext)

    return new MediaRecorder(stream)
  }, [audioDeviceId])

  const initializeRecorder = useCallback(async () => {
    try {
      const recorderInstance = await requestRecorder()
      setRecorder(recorderInstance)
    } catch (error) {
      analytics.track('mic_permission_denied', { error: error?.name || String(error) })
      console.error(error)
    }
  }, [requestRecorder])

  // this method gets fired when recorder.stop method is invoked
  /* Audio counts as real speech only if it was loud enough AND the level
     varied. Evaluated fresh from the frames of the current recording, so a
     previous attempt can never leave this stuck on. */
  const evaluateAudio = useCallback(() => {
    const loudest = loudestFrameRef.current
    const quietest = quietestFrameRef.current === Infinity ? 0 : quietestFrameRef.current

    const wasLoudEnough = loudest > AUDIO_VOLUME_THRESHOLD
    // Too few frames to judge variation (very short clip): fall back to
    // loudness alone rather than rejecting a legitimate short recording.
    const hasEnoughFrames = frameCountRef.current >= MIN_FRAMES_FOR_VARIANCE
    const varied = !hasEnoughFrames || loudest - quietest >= MIN_LEVEL_RANGE

    return wasLoudEnough && varied
  }, [])

  const handleData = useCallback(
    async (e) => {
      audioContext.close()
      if (!evaluateAudio()) {
        analytics.track('mic_test_failed', { reason: 'no_audible_content' })
        setErrorMessage(
          t("mic_test.error_msg")
        )
        setRecorder(null)
        return
      }
      analytics.track('mic_test_passed')

      // sending blob data for audioTestingModal component
      getBlobData(e.data)

    },
    [audioContext, getBlobData, evaluateAudio, t]
  )

  const startRecording = () => {
    setErrorMessage(false)
    setShowTroubleshoot(false)
    // Clear the previous attempt's frame stats before measuring a new one.
    loudestFrameRef.current = 0
    quietestFrameRef.current = Infinity
    frameCountRef.current = 0
    setIsRecording(true)
    onStatusChange("recording")
  }

  const stopRecording = () => {
    setIsRecording(false)
    onStatusChange("inactive")
  }

  useEffect(() => {
    if (recorder === null) {
      if (isRecording) {
        initializeRecorder()
      }
      return
    }

    // Manage recorder state.
    if (isRecording && recorder.state !== "recording") {
      recorder.start()

      // resuming audio context which give microphone volume info
      if (audioContext) audioContext.resume()
    } else if (!isRecording) {
      // execute stop only when state is inactive -> Android Fix
      recorder.state !== "inactive" && recorder.stop()

      // Suspends the progression of time in the audio context, temporarily halting audio hardware access and reducing CPU/battery usage in the process.
      if (audioContext) audioContext.suspend()
    }

    recorder.addEventListener("dataavailable", handleData)
    return () => {
      recorder.removeEventListener("dataavailable", handleData)
    }
  }, [recorder, isRecording, audioContext, handleData, initializeRecorder])

  const audioBlocks = Array.from({ length: 6 }, (_, index) => {
    const numberOfBlocksToColor = Math.round(volumeLevel / 6)

    return (
      <div
        key={index}
        className={`audio-block br-4 ${index < numberOfBlocksToColor ? "active-audio-block" : "inactive-audio-block"}`}
      />
    )
  })

  return (
    <div
      className={`${isMobile ? 'mt-14' : 'mt-10'} flex flex-col items-center`}
    >
      <AudioTroubleshootModal
        visible={showTroubleshoot}
        onClose={() => setShowTroubleshoot(false)}
      />
      {isRecording ? (
        <>
          <Button
            ref={buttonRef}
            type="primary"
            className="weight-400 size-12 br-20 width-140 flex justify-center items-center mt-10"
            onClick={stopRecording}
          >
            <div className="flex items-center mr-4">{audioBlocks}</div>
            {t("submit")}
          </Button>
        </>
      ) : (
        <>
          <Button
            type="primary"
            className="weight-400 size-12 br-20 width-80 mt-10 pt-6 pb-6"
            onClick={startRecording}
          >
            <span className="mic-icon">
              <MicrophoneIcon />
            </span>
          </Button>
          <p className={`size-14 color-primary font-medium opacity-70 ${isMobile ? 'mt-10' : 'mt-4'}`}>
            {t("mic_test.click_to_start")}
          </p>
          {!!errorMessage && (
            <div className="flex flex-col items-center mt-24">
              <p className={`color-primary font-medium opacity-70 width-${isMobile ? "300" : "400"} mb-12 error-text`}>{errorMessage}</p>
              <button
                className="troubleshoot-link size-14 weight-500 cursor-pointer"
                onClick={() => setShowTroubleshoot(true)}
              >{t("mic_test.troubleshoot.link")}</button>
              {/* Proceed Anyway is disabled: users must not continue when no audio is detected. Kept for future use. */}
              {/* <Button
                loading={loading}
                type="primary"
                className="weight-500 size-14 br-20 width-150"
                onClick={onSuccess}
              >{t("mic_test.proceed_anyway")}</Button> */}
            </div>
          )}
        </>
      )}
    </div>
  )
}
